import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, switchMap } from 'rxjs';
import { HelperService } from '../helper.service';
import RealTimeConnection from './real-time-connection';
import { RealTimeConnectionDeps } from "./real-time-connection.deps";
import RealTimeStreamConnection from './real-time-stream-connection';
import { disposeStreamSubscription, RealTimeStreamSubscriber } from './real-time-stream-subscriber';

@Injectable({
  providedIn: 'root'
})
export class RealTimeService {

  private eventListeners: { [key: string]: (data: unknown) => void; } = {};
  private eventObservers: { [key: string]: Set<(data: unknown) => void>; } = {};
  private eventDispatchers: { [key: string]: (data: unknown) => void; } = {};
  private streamSubscribers: { [key: string]: RealTimeStreamSubscriber; } = {};
  private streamConnections: { [key: string]: RealTimeStreamConnection; } = {};

  private readonly _steadyConnection: RealTimeConnection;

  constructor(
    private realTimeConnectionDeps: RealTimeConnectionDeps,
    private helperService: HelperService
  ) {
    this._steadyConnection = new RealTimeConnection(realTimeConnectionDeps);
    this.helperService.onUserIsActive(() => this.connectAll(), () => this.disconnectAll());
  }

  get steadyConnection() {
    return this._steadyConnection;
  }

  public connectAll(): Observable<void[]> {
    const connectionObservables: Observable<void>[] = [this.steadyConnection.connect(this.eventDispatchers, Object.values(this.streamSubscribers))];
    for (const streamName in this.streamConnections) {
      connectionObservables.push(this.streamConnections[streamName].connect());
    }
    return forkJoin(connectionObservables);
  }

  public disconnectAll(): Observable<unknown[]> {
    const connectionObservables: Observable<void>[] = [this.steadyConnection.disconnect()];
    for (const connectionName in this.streamConnections) {
      connectionObservables.push(this.streamConnections[connectionName].disconnect());
    }
    return forkJoin(connectionObservables);
  }

  public reconnectAll(): Observable<void[]> {
    return this.disconnectAll().pipe(switchMap(() => this.connectAll()));
  }

  public send(methodName: string, ...args: unknown[]): Observable<void> {
    return from(this.steadyConnection.connection.send(methodName, args));
  }

  public invoke(methodName: string, ...args: unknown[]): Observable<unknown> {
    return from(this.steadyConnection.connection.invoke(methodName, args));
  }

  public registerHandler<T>(methodName: string, listener: (data: T) => void): void {
    this.eventListeners[methodName] = listener;
    this.ensureEventDispatcher(methodName);
  }

  public unRegisterHandler(methodName: string): void {
    delete this.eventListeners[methodName];
    this.removeUnusedEventDispatcher(methodName);
  }

  /** Observe an event without replacing its state handler; survives connection rebuilds. */
  public observeEvent<T>(methodName: string): Observable<T> {
    return new Observable<T>(subscriber => {
      const listener = (data: unknown) => subscriber.next(data as T);
      const observers = this.eventObservers[methodName] ??= new Set();
      observers.add(listener);
      this.ensureEventDispatcher(methodName);
      return () => {
        observers.delete(listener);
        if (!observers.size) delete this.eventObservers[methodName];
        this.removeUnusedEventDispatcher(methodName);
      };
    });
  }

  private ensureEventDispatcher(methodName: string) {
    if (this.eventDispatchers[methodName]) return;
    const dispatcher = (data: unknown) => {
      this.eventListeners[methodName]?.(data);
      this.eventObservers[methodName]?.forEach(listener => listener(data));
    };
    this.eventDispatchers[methodName] = dispatcher;
    this.steadyConnection.connection?.on(methodName, dispatcher);
  }

  private removeUnusedEventDispatcher(methodName: string) {
    if (this.eventListeners[methodName] || this.eventObservers[methodName]?.size) return;
    const dispatcher = this.eventDispatchers[methodName];
    if (dispatcher) this.steadyConnection.connection?.off(methodName, dispatcher);
    delete this.eventDispatchers[methodName];
  }

  /**
   * Starts a steady stream for this subscriber, replacing whatever stream is already running on the
   * same hub method. Only one stream per hub method can be registered, so the previous one is torn
   * down first - otherwise its `next` keeps feeding the new caller's list and its `complete` clears
   * the new caller's loading flag.
   */
  public startSteadyStream(subscriber: RealTimeStreamSubscriber) {
    this.disposeRegisteredSteadyStream(subscriber.hubInfo.methodName, subscriber);

    const cleanup = () => this.disposeSteadyStream(subscriber);
    const subscriberOnComplete = subscriber.streamSubscriber.complete;
    subscriber.streamSubscriber.complete = () => {
      try {
        subscriberOnComplete?.();
      } finally {
        cleanup();
      }
    };

    const subscriberOnError = subscriber.streamSubscriber.error;
    subscriber.streamSubscriber.error = (err: unknown) => {
      try {
        subscriberOnError?.(err);
      } finally {
        cleanup();
      }
    };

    this.streamSubscribers[subscriber.hubInfo.methodName] = subscriber;
    return this.steadyConnection.stream(subscriber);
  }

  /**
   * Tears down the steady stream this subscriber owns. Safe to call more than once. A subscriber
   * that has already been superseded by a newer stream on the same hub method only ever disposes
   * its own (by then dead) subscription - it must not evict the registry entry that now belongs to
   * its replacement.
   */
  public disposeSteadyStream(subscriber: RealTimeStreamSubscriber) {
    if (this.streamSubscribers[subscriber.hubInfo.methodName] === subscriber) {
      this.disposeRegisteredSteadyStream(subscriber.hubInfo.methodName);
      return;
    }
    disposeStreamSubscription(subscriber);
  }

  /**
   * Drops whatever stream is registered for this hub method. `replacement` is the subscriber about
   * to take the slot; it is only used to recognise a caller that re-submits the very same subscriber
   * object, whose callbacks have to survive.
   */
  private disposeRegisteredSteadyStream(methodName: string, replacement?: RealTimeStreamSubscriber) {
    const registered = this.streamSubscribers[methodName];
    delete this.streamSubscribers[methodName];
    if (!registered) {
      return;
    }

    // `false` and not `!== true` on purpose here: this asks "was it subscribed and still running",
    // and a subscriber that never got subscribed (`undefined`) has nothing to silence.
    const wasLive = registered.streamSubscriber.closed === false;
    disposeStreamSubscription(registered);

    if (wasLive && registered !== replacement) {
      // We cancelled a stream that was still running, so anything signalr might still deliver on it
      // belongs to a request the caller has replaced or abandoned. Drop it instead of letting it
      // push rows into the new list or clear the new request's loading flag.
      registered.streamSubscriber.next = () => undefined;
      registered.streamSubscriber.complete = () => undefined;
      registered.streamSubscriber.error = () => undefined;
    }
  }

  public startStreamConnection(subscriber: RealTimeStreamSubscriber) {
    this.disposeStreamConnection(subscriber.hubInfo.methodName);
    const streamConnection = new RealTimeStreamConnection(
      this.realTimeConnectionDeps,
      subscriber
    );
    this.streamConnections[subscriber.hubInfo.methodName] = streamConnection;
    this.helperService.ifUserIsActiveNow(() => streamConnection.connect());
  }

  public disposeStreamConnection(methodName: string) {
    this.streamConnections[methodName]?.dispose();
    delete this.streamConnections[methodName];
  }
}
